import { ApiBaseService, type ApiServiceConfig } from "./ApiBaseService";
import AuthAccountService from "./AuthAccountService";

/**
 * This service is used to manage user authentications.
 *
 * [it handles]:
 * - user logins
 * - user registrations
 * - password resets
 * - email verification
 * - authenticated user retrieval
 */
export default class AuthService extends ApiBaseService {
    public account: AuthAccountService;

    constructor(config: ApiServiceConfig) {
        super(config);

        this.account = new AuthAccountService(config);
    }

    async register(
        first_name: string,
        last_name: string,
        name: string,
        email: string,
        password: string,
        password_confirmation: string,
        brand_data?: any,
        email_verified_at?: string,
        referral_partner?: string,
    ): Promise<any> {
        return await this.request(
            this.route("api.auth.register", "/auth/register"),
            {
                method: "post",
                body: {
                    first_name,
                    last_name,
                    name,
                    email,
                    password,
                    password_confirmation,
                    ...(brand_data
                        ? {
                              identifier: brand_data.id,
                              provider: brand_data.provider,
                          }
                        : {}),
                    email_verified_at,
                    referral_partner,
                },
            },
        );
    }

    async userCompletion(
        first_name: string,
        last_name: string,
        name: string,
        email: string,
        password: string,
        password_confirmation: string,
        token?: string,
    ): Promise<any> {
        return await this.request(
            this.route("api.auth.register", "/users/invite/{token}/register", {
                token,
            }),
            {
                method: "post",
                body: {
                    first_name,
                    last_name,
                    name,
                    email,
                    password,
                    password_confirmation,
                },
            },
        );
    }

    async forgotPassword(email: string): Promise<any> {
        return await this.request(
            this.route("api.auth.forgot", "/auth/forgot"),
            {
                method: "post",
                body: { email },
            },
        );
    }

    async getResetPassword(
        token: string,
    ): Promise<{ data: any; status: string }> {
        return await this.request(
            this.route(
                "api.auth.password.reset.data",
                "/auth/password/reset/{token}",
                {
                    token,
                },
            ),
            {
                method: "get",
            },
        );
    }

    async resetPassword(
        token: string,
        password: string,
        password_confirmation: string,
    ): Promise<{ data: any; message: string }> {
        return await this.request(
            this.route("api.auth.password.reset.save", "/auth/password/reset"),
            {
                method: "post",
                body: { token, password, password_confirmation },
            },
        );
    }

    async sendEmailVerification(): Promise<any> {
        return await this.request(
            this.route("api.auth.verify", "/auth/verify"),
            {
                method: "post",
            },
        );
    }
}
